/**
 * Language Detection and Utilities
 *
 * Advanced language detection, display names, and browser integration
 */

import { systemLogger } from '../logging';
import {
  type SupportedLanguage,
  isValidLanguage,
  LANGUAGE_NAMES,
  NATIVE_LANGUAGE_NAMES as _NATIVE_LANGUAGE_NAMES
} from './constants';
import {
  getLanguageFromURL,
  getLanguageFromCookie,
  getBrowserLanguage
} from './storage';

/**
 * Detect user's preferred language using priority chain
 * Priority: URL param → User profile (DB) → Cookie → Company default → Browser → English
 *
 * Note: User profile (from database) has higher priority than cookie because:
 * - Cookie = temporary UI preference (unauthenticated users)
 * - Database = permanent saved preference (authenticated users)
 * - Database should always override cookie for logged-in users
 */
export function detectUserLanguage(
  userLanguage?: string | null,
  companyDefaultLanguage?: string | null
): SupportedLanguage {
  // 1. URL parameter (highest priority - for testing/overrides)
  const urlLang = getLanguageFromURL();
  if (urlLang) {
    return urlLang;
  }

  // 2. User profile language (authenticated user's saved preference from database)
  if (userLanguage && isValidLanguage(userLanguage)) {
    return userLanguage;
  }

  // 3. Cookie (fallback for unauthenticated users or users without saved preference)
  const cookieLang = getLanguageFromCookie();
  if (cookieLang) {
    return cookieLang;
  }

  // 4. Company default language
  if (companyDefaultLanguage && isValidLanguage(companyDefaultLanguage)) {
    return companyDefaultLanguage;
  }

  // 5. Browser language
  const browserLang = getBrowserLanguage();
  if (browserLang) {
    return browserLang;
  }

  // 6. Fallback to English
  return 'en';
}

/**
 * Check if browser language is supported
 */
export function isBrowserLanguageSupported(): boolean {
  const browserLang = getBrowserLanguage();
  return browserLang !== null;
}

/**
 * Get full browser language (including region)
 */
export function getFullBrowserLanguage(): string | null {
  try {
    return navigator.language || null;
  } catch (error) {
    systemLogger.warn('Language detection failed', error);
    return null;
  }
}

/**
 * Get language display name
 */
export function getLanguageDisplayName(languageCode: string): string {
  if (isValidLanguage(languageCode)) {
    return LANGUAGE_NAMES[languageCode];
  }
  return languageCode; // Return as-is if not recognized
}

/**
 * Get language fallback chain for missing translations
 */
export function getLanguageFallbackChain(language: SupportedLanguage): SupportedLanguage[] {
  const chain: SupportedLanguage[] = [language];

  // Add English as fallback if not already the primary language
  if (language !== 'en') {
    chain.push('en');
  }

  return chain;
}